import { RAD, WALL_THICKNESS, WALL_MOUNT_OFFSET } from './constants.js';
import { blocked, distToSeg, wallIntersection } from './geometry.js';
import { sceneWalls } from './siteGeometry.js';
import { isCeilingCamera, isPoleCamera, wallMountOffset, wallPlateRise } from './cameraMounts.js';

export function wallSupports(wall, height) {
  return wall.kind === 'solid' && !wall.objectKind && (wall.bottom || 0) < height - 0.05 && wall.h >= height + 0.05;
}

export function wallAnchor(wall, x, y) {
  const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1, len2 = dx * dx + dy * dy;
  if (len2 < 1e-8) return null;
  const t = ((x - wall.x1) * dx + (y - wall.y1) * dy) / len2;
  if (t < 0 || t > 1) return null;
  return { x: wall.x1 + t * dx, y: wall.y1 + t * dy };
}

/** Resolve a real supporting wall for both saved plans and newly made plans. */
export function findWallMount(S, cam) {
  if (isCeilingCamera(S, cam) || isPoleCamera(S, cam)) return null;
  let best = null;
  for (const wall of sceneWalls(S)) {
    if (!wallSupports(wall, cam.h)) continue;
    const anchor = wallAnchor(wall, cam.x, cam.y);
    if (!anchor) continue;
    const distance = Math.hypot(cam.x - anchor.x, cam.y - anchor.y);
    const offset = wallMountOffset(S.models?.find(m => m.id === cam.modelId), cam, wall);
    if (distance > Math.max(offset, WALL_MOUNT_OFFSET) + 0.09 || distance < (wall.thickness ?? WALL_THICKNESS) / 2 + 0.02) continue;
    if (!best || distance < best.distance) best = { wall, ...anchor, distance };
  }
  return best;
}

export function validWallPose(S, cam, wall) {
  // A bracket needs a supporting wall, not a floor beneath its lens. In
  // particular, upper-storey exterior faces sit outside the floor polygon.
  // Coverage boundaries belong to sampling/planning, not mount validity.
  if (wall.mountRule === 'prohibited' || !wallSupports(wall, cam.h)) return false;
  const model = S.models?.find(m => m.id === cam.modelId);
  if (!wallSupports(wall, cam.h + wallPlateRise(model, cam))) return false;
  const anchor = wallAnchor(wall, cam.x, cam.y);
  if (!anchor) return false;
  const dx = cam.x - anchor.x, dy = cam.y - anchor.y;
  const rise = wallPlateRise(model, cam), walls = sceneWalls(S);
  if (rise && blocked(cam.x, cam.y, cam.h, cam.x, cam.y, cam.h + rise, walls)) return false;
  const offset = wallMountOffset(model, cam, wall);
  // Existing plans retain their saved lens coordinates until moved/replanned.
  if (Math.min(Math.abs(Math.hypot(dx, dy) - offset), Math.abs(Math.hypot(dx, dy) - WALL_MOUNT_OFFSET)) > 0.015) return false;
  const fx = Math.cos(cam.pan * RAD), fy = Math.sin(cam.pan * RAD);
  if (dx * fx + dy * fy <= 0.02) return false;
  for (const other of walls) {
    if (other.roofPts || other.wallPts) { if (wallIntersection(cam.x, cam.y, cam.h, cam.x, cam.y, cam.h, other)) return false; continue; }
    if ((other.bottom || 0) > cam.h + 0.1) continue;
    if (other.h < cam.h - 0.1) continue;
    const ownWall = (other.sourceId || other.id) === (wall.sourceId || wall.id);
    if (distToSeg(cam.x, cam.y, other.x1, other.y1, other.x2, other.y2) < (other.thickness ?? WALL_THICKNESS) / 2 + (ownWall ? 0.02 : 0.1)) return false;
    // A bracket must not pass through another wall, including glazing.
    if ((other.sourceId || other.id) !== (wall.sourceId || wall.id) && blocked(anchor.x, anchor.y, cam.h, cam.x, cam.y, cam.h, [{ ...other, kind: 'solid' }])) return false;
    if (rise && !ownWall && blocked(anchor.x, anchor.y, cam.h + rise, cam.x, cam.y, cam.h + rise, [{ ...other, kind: 'solid' }])) return false;
  }
  return !blocked(cam.x, cam.y, cam.h, cam.x + fx * 0.75, cam.y + fy * 0.75, cam.h, sceneWalls(S));
}

/** Nearest installable wall face, preserving the clicked side when both are valid. */
export function snapWallCamera(S, x, y, height = 2.8, maxDistance = 1.25, camera = {}) {
  let best = null;
  for (const wall of sceneWalls(S)) {
    if (!wallSupports(wall, height) || wall.mountRule === 'prohibited') continue;
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1, length = Math.hypot(dx, dy);
    if (length < 0.4) continue;
    const inset = Math.min(0.35, length / 2);
    const along = Math.max(inset, Math.min(length - inset, ((x - wall.x1) * dx + (y - wall.y1) * dy) / length));
    const anchor = { x: wall.x1 + dx / length * along, y: wall.y1 + dy / length * along };
    const offset = wallMountOffset(S.models?.find(m => m.id === camera.modelId), camera, wall);
    for (const sign of [1, -1]) {
      const nx = -dy / length * sign, ny = dx / length * sign;
      const pose = { ...camera, x: anchor.x + nx * offset, y: anchor.y + ny * offset, h: height, pan: (Math.atan2(ny, nx) / RAD + 360) % 360 };
      const distance = Math.hypot(x - pose.x, y - pose.y);
      if (distance > maxDistance || (best && distance >= best.distance) || !validWallPose(S, pose, wall)) continue;
      best = { ...pose, wall, distance };
    }
  }
  return best;
}
